import type { CopySightBoundingBox, CopySightDetection } from '@/types/copysight';
import { cn } from '@/lib/utils';

// CopySight boxes are fractions (0-1) of the image width/height, measured from
// the top-left corner. Clamped so a box never draws outside the picture.
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

const toPercentBox = (box: CopySightBoundingBox) => {
  const left = clamp01(box.x);
  const top = clamp01(box.y);
  return {
    left: `${left * 100}%`,
    top: `${top * 100}%`,
    width: `${(clamp01(box.x + box.width) - left) * 100}%`,
    height: `${(clamp01(box.y + box.height) - top) * 100}%`,
  };
};

interface CopySightImageProps {
  src: string;
  alt: string;
  detections?: CopySightDetection[];
  showFlagged: boolean;
  className?: string;
}

// An image with the CopySight-flagged areas drawn on top. The wrapper shrinks to
// the rendered image, so percentage positions line up with the picture.
export default function CopySightImage({ src, alt, detections, showFlagged, className }: CopySightImageProps) {
  const flagged = detections?.flatMap((d, i) => (d.boundingBox ? [{ index: i, box: d.boundingBox, name: d.name }] : [])) ?? [];

  return (
    <div className="relative w-fit max-w-full">
      <img src={src} alt={alt} className={cn('block max-w-full object-contain rounded-lg', className)} />
      {showFlagged &&
        flagged.map(({ index, box, name }) => (
          <div
            key={index}
            title={name || `Detection ${index + 1}`}
            className="absolute border-2 border-red-500 bg-red-500/10 pointer-events-none"
            style={toPercentBox(box)}
          >
            <span className="absolute -top-px -left-px rounded-br bg-red-500 px-1 text-[10px] font-semibold leading-4 text-white">
              {index + 1}
            </span>
          </div>
        ))}
    </div>
  );
}
